import { useCallback } from 'react'

import { ProfilingRequestSource } from 'clickNgo/configs/profiling/common'
import { ContactStatus, ContactWorkflowStatus, IContact } from 'clickNgo/entities/contact'
import { updateContact } from 'clickNgo/entities/contact/api'
import { mapContactModifiedValues } from 'clickNgo/entities/contact/api/mapping/mapContactModifiedValues'
import { getJSendErrorMessages } from 'clickNgo/helpers/api'
import { I375Form } from 'clickNgo/models/crud/forms'
import { useGetFormDataField } from 'cng-common/features/form'

import { ContactEditorFormKeys } from '../configs'
import { ContactEditorSettingFormFields, TContactEditorData, THandlerResult } from '../models'

type TParams = {
  contact: IContact
  onContactUpdate: (contact: IContact) => Promise<void> | void
}

export function useContactInactivation({ contact, onContactUpdate }: TParams) {
  const [actualData, updateActualData] = useGetFormDataField<TContactEditorData>(
    ContactEditorFormKeys.settings,
    ContactEditorSettingFormFields.ActualData
  )

  const [, setErrorMessages] = useGetFormDataField<string[]>(
    ContactEditorFormKeys.settings,
    ContactEditorSettingFormFields.ErrorMessages
  )
  const [, setIsLoading] = useGetFormDataField<boolean>(
    ContactEditorFormKeys.settings,
    ContactEditorSettingFormFields.IsLoading
  )

  const onContactInactivateHandler = useCallback<() => Promise<THandlerResult>>(async () => {
    setIsLoading(true)
    setErrorMessages([])

    const request: IContact = {
      ...contact,
      status: ContactStatus.Inactive,
      workflowStatus: ContactWorkflowStatus.New,
      source: ProfilingRequestSource.ClickAndGoPortal,
      modifiedValues: ['status'],
    }

    const response = await updateContact(request, { forceRoles: true })

    if (response) {
      switch (response.status) {
        case 'fail': {
          setIsLoading(false)
          setErrorMessages(getJSendErrorMessages(response))
          return {
            hasError: true,
          }
        }
        case 'error': {
          setIsLoading(false)
          setErrorMessages(response.message ? [response.message] : [])
          return {
            hasError: true,
          }
        }
        case 'success': {
          const updatedContact: IContact = {
            ...request,
            ...response.data,
            modifiedValues: mapContactModifiedValues(
              response.data.modifiedValues.split(',') as (keyof I375Form)[]
            ),
            modified: new Date().toISOString(),
          }

          await onContactUpdate(updatedContact)

          updateActualData({
            ...actualData,
            contact: updatedContact,
          })
          break
        }
      }

      setIsLoading(false)
      return {
        hasError: false,
      }
    } else {
      setErrorMessages(['Something went wrong!'])
      setIsLoading(false)
      return {
        hasError: true,
      }
    }
  }, [actualData, contact, onContactUpdate, setErrorMessages, setIsLoading, updateActualData])

  return {
    onContactInactivateHandler,
  }
}
